import * as React from "react";
import { useState, useContext, useEffect } from "react";
import { makeStyles, Theme, createStyles } from "@mui/styles";
import {ValueContext} from '~common/components/form-def-field-fc';
import {ICGFormControl} from '~common/components/icg-form-control';
import { ValidationField }  from "~common/components/fields/validation-field-fc";
import {cloneDeep} from 'lodash'
import {
  Input,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { IMaskInput } from 'react-imask';
import { Address1Context,
  Address2Context,
  Address3Context,
  CityContext,
  CountyContext,
  StateContext,
  PostalCodeContext,
  CountryContext }  from "~common/components/fields/address-field-fc";
  

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textField: {
      width: "100%",
    },
    displayText: {
      flexShrink: 1,
      width: "100%",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    },
    displayTextWrap: {
      width: "100%",
      whiteSpace: "normal"
    },
  })
);

interface TextProps {
  fieldDefinition: any;
  policyMessages?: any;
  contextType?: string;
  readOnlyOverride?: boolean;
  error?: any;
  onAccessRootData?: any;
  path?: string;
  handleTriggerChange?: any;
  triggerValidationAtURLFunc?: Function
}

const TextMaskCustom = React.forwardRef<HTMLElement, CustomProps>(
  function TextMaskCustom(props, ref) {
    const {onChange,mask, ...other } = props;
    return (
      <IMaskInput
        {...other}
        padFractionalZeros={false}
        // value={localVal}
        onChange={onChange}
        // onBlur={onBlur2}      
        radix={'.'}
        mapRadix={['.']}
        mask={mask}
        definitions={{
          '#': /[0-9]/,
          '9': /[0-9]/,
        }}
        inputRef={ref}
        // autoFocus={true}
        onAccept={(value: any) => onChange({ target: { name: props.name, value } })}
        overwrite
      />
    );
  },
);  

export const TextField: React.FC<TextProps> = ({
  fieldDefinition,
  policyMessages,
  contextType,
  onAccessRootData,
  path,
  readOnlyOverride,
  handleTriggerChange,
  triggerValidationAtURLFunc
}) => {
  const classes = useStyles();
  
  var autofillString =
      navigator.userAgent.indexOf("Chrome") > -1 ? "chrome-off" : "off";

  // Setting the inital value and setValue hook based on the context passed in from form def field, instead of useState
  // setFieldVal will pass the value up to the parent component that houses the context.
  var parentContext = useContext(ValueContext);

  if (contextType && contextType != "") {
    switch (contextType) {
      case "address1":
        parentContext = useContext(Address1Context);
        break
      case "address2":
        parentContext = useContext(Address2Context);
        break
      case "address3":
        parentContext = useContext(Address3Context);
        break
      case "city":
        parentContext = useContext(CityContext);
        break
      case "county":
        parentContext = useContext(CountyContext);
        break
      case "state":
        parentContext = useContext(StateContext);
        break
      case "postalCode":
        parentContext = useContext(PostalCodeContext);
        break
      case "country":
        parentContext = useContext(CountryContext);
        break
    }
  } 

  const [fieldVal, setFieldVal] = parentContext;

  const [localVal, setLocalVal] = useState(fieldVal || null);
  const [localMessageText, setLocalMessageText] = useState("");
  const [localMessageClass, setLocalMessageClass] = useState("");
  const [showPassword, setShowPassword] = useState(false)

  // if ((!fieldVal || fieldVal == "") && fieldDefinition.defaultValue) {
  //   setFieldVal()
  // }


  const onChange = (event) => {
    setLocalVal(event.target.value);
  }


  const onBlur = (event) => {
    setFieldVal(event.target.value);
  }


  const onKeyPress = (e: any) => {
    let valid = true;
    if (
      fieldDefinition &&
      fieldDefinition.allowedCharacters &&
      fieldDefinition.allowedCharacters.length > 0
    ) {
      valid = isKeypressCharValid(e, fieldDefinition.allowedCharacters);
    }

    if (!valid) {
      e.preventDefault();
    }

    return valid;
  };

  const onPaste = (event: any) => {
    // const {value} = this.state
    var bufferVal = cloneDeep(localVal)
    if (fieldDefinition.allowedCharacters) {
      navigator.clipboard
        .readText()
        .then((text) => {
          var newVal = text.trim();
          newVal.split("").forEach(function (c) {
            if ((fieldDefinition.allowedCharacters).indexOf(c) == -1) {
              newVal = null;
              setLocalVal(bufferVal)
              setLocalMessageText("Invalid Character(s) detected in paste content");
              setLocalMessageClass("warning")
            }
          })
          if (newVal) {
            // Reset field value to newly trimmed value
            setFieldVal(newVal)
          }

        })
        .catch((err) => {
          console.error("Failed to read clipboard contents: ", err);
        });

        
    }
  };

  const isKeypressCharValid = (e, chars) => {
    e = e || window.event;

    // Allow delete, tab, enter and escape keys through
    if (/^(8|9|13|27)$/.test("" + e.charCode)) {
      return true;
    }

    var charCode = typeof e.which == "number" ? e.which : e.charCode;
    var charTyped = String.fromCharCode(charCode);
    return chars.indexOf(charTyped) > -1;
  };

  const handleClickShowPasssword = () => {
    var _showPassword = cloneDeep(showPassword)
    setShowPassword(!_showPassword)
  }

  const getTextFieldType = (fd: any) => {
    if (!fd.variant || fd.variant == "") {
      return "text";
    }

    if (fd.variant == "Password" && !showPassword) {
      return "password";
    }

    return "text";
  };

  const handleTriggerValidationAtURL = () => {
    if (fieldDefinition.triggerValidationAtURL && triggerValidationAtURLFunc) {
      triggerValidationAtURLFunc(localVal) 
       }
  }

  // Return an obvious error so we change the form to the new chips field
  if (fieldDefinition.variant && fieldDefinition.variant.toLowerCase().startsWith('chip')) {
    console.warn("Chip Field Deprecated. Please see Autocomplete Free Solo for equivilant functionality", fieldDefinition)
    return <div>Chip Field Deprecated. Please see Autocomplete Free Solo for equivilant functionality</div>
  }

  // Listen for changes from the parent def
  useEffect(() => {

    if (fieldVal){
      setLocalVal(fieldVal);
    } else {
      setLocalVal(null);
    }
    
  }, [fieldVal])

  
  if (fieldDefinition.mode == "Display") {
    var textClass = classes.displayText;
    if (fieldDefinition.styleAttributes && fieldDefinition.styleAttributes.whiteSpace == "normal") {
      textClass = classes.displayTextWrap;
    }
    return (
    <ICGFormControl fieldDefinition={fieldDefinition} policyMessages={policyMessages} readOnlyOverride={readOnlyOverride}> 
        <div name={fieldDefinition.key} className={textClass} data-field-value={localVal || ""}>
          {localVal || "--"}
        </div>
    </ICGFormControl>
    );
  }
  var htmlProps = {}
  if(fieldDefinition.maxLength) {
    htmlProps["maxLength"] = fieldDefinition.maxLength;
  }

  if (fieldDefinition.mask) {
    htmlProps["mask"] = fieldDefinition.mask;
  }

  htmlProps["autoComplete"] = autofillString

  var endAdornment = null
  if (fieldDefinition && fieldDefinition.validationAttributes && fieldDefinition.validationAttributes.validationURL) {
    endAdornment = (
      <ValidationField
      fieldDefinition={fieldDefinition}
      path={path} 
      onAccessRootData={onAccessRootData}
      readOnlyOverride={readOnlyOverride}
    />
    )
  }

  if (fieldDefinition && fieldDefinition.variant == "Password") {
    endAdornment = (
      <InputAdornment position="end">
        <IconButton size="large" tabIndex={-1} onClick={handleClickShowPasssword}>
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    );
  }

  if (!endAdornment && fieldDefinition.triggerValidationAtURL && fieldDefinition.triggerValidationAtURLAttributes && fieldDefinition.triggerValidationAtURLAttributes.allowManualExecution && fieldDefinition.mode != "Read Only" && !readOnlyOverride) {
    endAdornment = (<InputAdornment position="end">
        <IconButton className="searchIcon" size="large" tabIndex={-1} onClick={handleTriggerValidationAtURL}>
          <SearchOutlinedIcon />
        </IconButton>
      </InputAdornment>)
  }

  htmlProps["data-roi-path"] = path;
  htmlProps["data-field-key"] = fieldDefinition.key;
  htmlProps["data-field-path"] = path;
  htmlProps["data-field-type"] = fieldDefinition.type;

  return (

    <ICGFormControl readOnlyOverride={readOnlyOverride} fieldDefinition={fieldDefinition} policyMessages={policyMessages} fieldControlMessage={localMessageText} fieldControlClass={localMessageClass}> 
    <div >
      <Input
      type={getTextFieldType(fieldDefinition)}
      placeholder={fieldDefinition.placeholder || ""}
      className={classes.textField}
      onKeyPress={onKeyPress}
      value={localVal || ""}
      name={fieldDefinition.key}
      inputProps={htmlProps}
      endAdornment={endAdornment}
      inputComponent={fieldDefinition.mask ? TextMaskCustom as any : undefined}
      onPaste={fieldDefinition.allowedCharacters ? onPaste : null}
      onChange={onChange}
      onBlur={onBlur}
      multiline={fieldDefinition.variant == "TextArea" ? true : false}
      rows={
        fieldDefinition.variant == "TextArea" && fieldDefinition.textAreaRowCount
          ? fieldDefinition.textAreaRowCount
          : 2
      }
    />
    </div>
    </ICGFormControl> 
  )
};

export default TextField;
